// CB:BEGIN impersonation-context
/**
 * ImpersonationContext
 *
 * Allows admins to enter a user session (homeowner or contractor) for
 * debugging and support. State is held in sessionStorage so it survives
 * in-tab refreshes but is cleared when the browser tab is closed.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';

const SESSION_KEY = 'cb_impersonation';
const TTL_MS = 30 * 60 * 1000; // 30 minutes

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ImpersonatedUser {
  id: string;
  email: string;
  role: 'HOMEOWNER' | 'CONTRACTOR';
  displayName: string;
  homeownerId: string | null;
  contractorId: string | null;
}

interface StoredState {
  token: string;
  targetUser: ImpersonatedUser;
  startedAt: number;
}

interface ImpersonationContextValue {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  impersonationToken: string | null;
  /** Call with the DB user.id of the target. Returns the impersonated user. */
  enterSession: (targetUserId: string) => Promise<ImpersonatedUser>;
  /** Logs exit on the backend and clears local state. */
  exitSession: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

function readStoredState(): StoredState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: StoredState = JSON.parse(raw);
    if (!parsed.token || !parsed.targetUser || !parsed.startedAt) return null;
    if (Date.now() - parsed.startedAt > TTL_MS) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getToken } = useAuth();
  const [state, setState] = useState<StoredState | null>(() => readStoredState());

  // Re-validate stored state on mount (handles page reload)
  useEffect(() => {
    const stored = readStoredState();
    setState(stored);
  }, []);

  const persist = useCallback((next: StoredState | null) => {
    setState(next);
    if (next) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, []);

  const enterSession = useCallback(async (targetUserId: string): Promise<ImpersonatedUser> => {
    const token = await getToken();
    const res = await fetch(getApiUrl('/api/admin/impersonate'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ targetUserId }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as any).error || `Impersonation failed (${res.status})`);
    }
    const data = await res.json();
    const next: StoredState = {
      token: data.token,
      targetUser: data.targetUser,
      startedAt: Date.now(),
    };
    persist(next);
    return data.targetUser as ImpersonatedUser;
  }, [getToken, persist]);

  const exitSession = useCallback(async (): Promise<void> => {
    const currentState = state;
    persist(null);
    if (!currentState) return;
    try {
      const token = await getToken();
      await fetch(getApiUrl('/api/admin/impersonate/exit'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ token: currentState.token }),
      });
    } catch (e) {
      // Exit audit logging failure is non-fatal
      console.error('[ImpersonationContext] Exit API call failed:', e);
    }
  }, [state, getToken, persist]);

  const value: ImpersonationContextValue = {
    isImpersonating: state !== null,
    impersonatedUser: state?.targetUser ?? null,
    impersonationToken: state?.token ?? null,
    enterSession,
    exitSession,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error('useImpersonation must be used within ImpersonationProvider');
  return ctx;
}
// CB:END impersonation-context
